import type { ChecklistItem } from "@hamecho/content-schema";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useChecklistTemplates } from "@/hooks/useChecklistQueries";
import type { ChecklistTemplate } from "@/types/proto/api/v1/checklist_service_pb";

/**
 * TemplateSelectDialog - Select and apply a checklist template
 *
 * Features:
 * - Browse available templates
 * - Preview template items
 * - Apply selected template to current checklist
 */
interface TemplateSelectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (items: ChecklistItem[]) => void;
}

export function TemplateSelectDialog({ open, onOpenChange, onApply }: TemplateSelectDialogProps) {
  const { data, isLoading } = useChecklistTemplates();
  const [selectedTemplateUid, setSelectedTemplateUid] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedTemplateUid("");
      setSearchQuery("");
    }
  }, [open]);

  // Filter templates by search query
  const filteredTemplates =
    data?.templates.filter(
      (t) => t.title.toLowerCase().includes(searchQuery.toLowerCase()) || t.description.toLowerCase().includes(searchQuery.toLowerCase()),
    ) || [];

  const selectedTemplate = filteredTemplates.find((t) => t.uid === selectedTemplateUid);

  const handleApply = () => {
    if (!selectedTemplate) {
      toast.error("请选择一个模板");
      return;
    }

    // Convert proto items to checklist items
    const items: ChecklistItem[] = selectedTemplate.items.map((item, index) => ({
      id: `item-${index}-${Date.now()}`,
      text: item.text,
      checked: false,
      category: item.category || undefined,
    }));

    onApply(items);
    onOpenChange(false);
    setSelectedTemplateUid("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>应用检查清单模板</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Template List */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="search">搜索模板</Label>
              <Input id="search" placeholder="输入标题或描述..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>

            <ScrollArea className="h-[400px] border rounded-lg p-2">
              {isLoading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="text-center p-8 text-muted-foreground">{searchQuery ? "没有找到匹配的模板" : "暂无可用模板"}</div>
              ) : (
                <div className="space-y-2">
                  {filteredTemplates.map((template) => (
                    <TemplateListItem
                      key={template.uid}
                      template={template}
                      selected={selectedTemplateUid === template.uid}
                      onSelect={() => setSelectedTemplateUid(template.uid)}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Template Preview */}
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold mb-2">模板预览</h3>

            {!selectedTemplate ? (
              <div className="flex items-center justify-center h-[400px] text-muted-foreground">请从左侧选择一个模板</div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  <div>
                    <Label className="text-muted-foreground">标题</Label>
                    <p className="font-medium">{selectedTemplate.title}</p>
                  </div>

                  <div>
                    <Label className="text-muted-foreground">描述</Label>
                    <p className="text-sm">{selectedTemplate.description || "暂无描述"}</p>
                  </div>

                  <div>
                    <Label className="text-muted-foreground">分类</Label>
                    <p className="text-sm">
                      {
                        {
                          1: "价值投资",
                          2: "技术分析",
                          3: "短线交易",
                          4: "自定义",
                          0: "未分类",
                        }[selectedTemplate.category]
                      }
                    </p>
                  </div>

                  <div>
                    <Label className="text-muted-foreground">检查项 ({selectedTemplate.items.length})</Label>
                    <div className="mt-2 space-y-2">
                      {selectedTemplate.items.map((item, index) => (
                        <div key={index} className="flex items-start gap-2 text-sm">
                          <Checkbox checked={item.checked} disabled />
                          <span className="flex-1">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </ScrollArea>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button onClick={handleApply} disabled={!selectedTemplate}>
            应用模板
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * TemplateListItem - Display a single template in the list
 */
interface TemplateListItemProps {
  template: ChecklistTemplate;
  selected: boolean;
  onSelect: () => void;
}

function TemplateListItem({ template, selected, onSelect }: TemplateListItemProps) {
  const categoryLabel = {
    1: "价值投资",
    2: "技术分析",
    3: "短线交易",
    4: "自定义",
    0: "未分类",
  }[template.category];

  return (
    <div
      onClick={onSelect}
      className={`
        p-3 rounded-lg border cursor-pointer transition-colors
        ${selected ? "bg-primary/10 border-primary" : "hover:bg-muted"}
      `}
    >
      <div className="flex items-start justify-between mb-1">
        <h4 className="font-medium text-sm">{template.title}</h4>
        <span className="text-xs px-2 py-0.5 bg-secondary rounded-full">{categoryLabel}</span>
      </div>

      <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{template.description || "暂无描述"}</p>

      <div className="text-xs text-muted-foreground">{template.items.length} 个检查项</div>
    </div>
  );
}
